import * as THREE from 'three/webgpu';
import type { RawPointerEventPayload } from '../../viewer/moudles/EventManager';
import { isFeatureFace, isFeatureLine, isFeaturePoint } from '../../mesh/feature';
import type { ImportedModelRaycastResolver } from '../../imported-model/selection/ImportedModelRaycastResolver';
import { isDatumAxis } from '../../reference/DatumAxis';
import { isDatumPlaneObject } from '../../reference/DatumPlane';
import { isDatumPoint } from '../../reference/DatumPoint';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { AssemblyEntitySelectionHandler } from './AssemblyEntitySelectionHandler';

//------ 装配体模式下基准点线面 开始------
/**
 * 判断对象是否可作为装配体模式下基准轴参考实体。
 *
 * @param object 场景对象。
 * @returns 是否允许作为装配体基准轴引用。
 */
function isAssemblyReferenceAxisSelectableObject(object: THREE.Object3D): boolean {
    return isFeatureFace(object)
        || isFeatureLine(object)
        || isFeaturePoint(object)
        || isDatumAxis(object)
        || isDatumPoint(object)
        || isDatumPlaneObject(object);
}

/**
 * 装配体模式下基准轴选择处理器。
 *
 * 职责：
 * 1. 仅在装配体基准轴命令期间处理拾取与移入高亮；
 * 2. 保留 imported-model 二阶段代理拾取与 hover 代理复用；
 * 3. 与普通基准轴命令分离，避免普通模式依赖装配体选择链。
 */
export class AssemblyReferenceAxisEntitySelectionHandler extends AssemblyEntitySelectionHandler {
    /**
     * 创建装配体模式下基准轴选择处理器。
     *
     * @param entitySelectionManager 实体选择管理器。
     * @param importedModelRaycastResolver 导入模型射线解析器。
     */
    public constructor(
        entitySelectionManager: EntitySelectionManager,
        importedModelRaycastResolver: ImportedModelRaycastResolver,
    ) {
        super(entitySelectionManager, importedModelRaycastResolver, { assemblyModeEnabled: false });
    }

    /**
     * 处理鼠标按下。
     * @param payload 原始事件。
     */
    public override onMouseDown(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标移动。
     * @param payload 原始事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (payload.buttons !== 0) {
            this.clearHoverState();
            return;
        }
        this.queueHoverInput(payload.clientX, payload.clientY);
    }

    /** @inheritdoc 使用当前模式的候选规则消费最新悬停坐标。 */
    public override consumeHoverInput(clientX: number, clientY: number): void {
        const hoveredPick = this.pickEntityAt(
            clientX,
            clientY,
            this.collectAssemblyReferenceAxisSelectionTargets(),
        );
        this.updateHoverState(this.resolveHoverPickWithSelectionExclusion(hoveredPick));
    }

    /**
     * 处理鼠标抬起。
     * @param payload 原始事件。
     */
    public override onMouseUp(payload: RawPointerEventPayload): void {
        void payload;
    }

    /**
     * 处理鼠标点击。
     * @param payload 原始事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.entitySelectionManager.getSuppressNextClick()) {
            this.entitySelectionManager.setSuppressNextClick(false);
            return;
        }

        const rawPicked = this.resolveAssemblyReferenceAxisSelectionPick(payload.clientX, payload.clientY);
        const picked = this.resolveClickSelectionPick(rawPicked);
        if (!picked) {
            return;
        }

        if (!isAssemblyReferenceAxisSelectableObject(picked.object)) {
            return;
        }

        const viewer = this.entitySelectionManager.renderer;
        const accepted = viewer.referenceAxisController.handleSelectionObject(picked.object);
        if (!accepted) {
            viewer.drawingNoticeCallback?.('当前实体不支持作为基准轴参考', 'warning');
        }
    }

    /**
     * 收集框选候选对象。
     * 说明：
     * 1. 装配体基准轴命令不支持框选流程；
     * 2. 返回空数组以禁用框选拾取路径，确保行为与命令约束一致。
     *
     * @returns 空候选列表。
     */
    protected override collectBoxSelectionTargets(): THREE.Object3D[] {
        return [];
    }

    /**
     * 解析当前点击应消费的装配体基准轴引用命中。
     *
     * 设计说明：
     * 1. imported-model 代理对象在装配体参考命令里必须沿用当前 hover 已挂载的同一个实例；
     * 2. 这样选择中心写入 selected 后，代理保留控制器才能持续保住该点线面对象；
     * 3. 若当前 hover 不是合法装配体基准轴引用，再回退到正常拾取流程。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @returns 当前点击应消费的命中结果。
     */
    private resolveAssemblyReferenceAxisSelectionPick(
        clientX: number,
        clientY: number,
    ): THREE.Intersection<THREE.Object3D> | null {
        const hoveredPick = this.entitySelectionManager.getHoveredPick();
        if (hoveredPick?.object && isAssemblyReferenceAxisSelectableObject(hoveredPick.object)) {
            return hoveredPick;
        }

        return this.pickEntityAt(
            clientX,
            clientY,
            this.collectAssemblyReferenceAxisSelectionTargets(),
        );
    }

    /**
     * 收集测量叠加层点击候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureClickSelectionTargets(): THREE.Object3D[] {
        return this.collectAssemblyReferenceAxisSelectionTargets();
    }

    /**
     * 收集测量叠加层移入候选对象。
     *
     * @returns 候选对象列表。
     */
    public collectMeasureHoverSelectionTargets(): THREE.Object3D[] {
        return this.collectAssemblyReferenceAxisSelectionTargets();
    }

    /**
     * 收集装配体基准轴模式的可拾取对象。
     *
     * 设计说明：
     * 1. 装配体基准轴命令链路当前接收特征点/线/面与正式基准点/轴/面；
     * 2. imported-model 对象作为二阶段代理拾取入口保留在候选集中；
     * 3. 这样可以让装配体拓扑代理和正式基准对象共用同一条控制器消费链。
     *
     * @returns 可参与拾取的对象列表。
     */
    private collectAssemblyReferenceAxisSelectionTargets(): THREE.Object3D[] {
        return this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()).filter((candidate) => (
            isAssemblyReferenceAxisSelectableObject(candidate)
            || this.importedModelRaycastResolver.isImportedModelObject(candidate)
        ));
    }
}
//------ 装配体模式下基准点线面 结束------
